import React, { useEffect, useState } from "react";
import { closeModal } from "$stores/modal";
import { initiativesApi } from "../api";
import DateInput from "$components/form-inputs/date-picker";
import { InitiativeDetailsStore } from "$stores/initiative-details";

const AddActionItemModal = ({ initiativeId, onSave }) => {
    const collaborators = InitiativeDetailsStore.useState((s) => s.collaborators);
    const [text, setText] = useState("");
    const [dueDate, setDueDate] = useState(null);
    const [assigneeId, setAssigneeId] = useState("");
    const [projectKey, setProjectKey] = useState("");
    const [shortlist, setShortlist] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        initiativesApi
            .getShortlist(initiativeId, controller.signal)
            .then((res) => {
                if (controller.signal.aborted) return;
                setShortlist(res || []);
            })
            .catch(() => {});
        return () => controller.abort();
    }, [initiativeId]);

    const handleCancel = () => closeModal();

    const handleSave = async () => {
        if (!text.trim()) return;
        setSaving(true);
        setError(null);
        try {
            await initiativesApi.addFollowUp(initiativeId, {
                commitmentText: text.trim(),
                dueDate: dueDate ? dueDate.toISOString() : null,
                assignedToUserId: assigneeId || null,
                projectKey: projectKey ? Number(projectKey) : null,
            });
            closeModal();
            onSave();
        } catch {
            setError("Failed to add action item. Please try again.");
            setSaving(false);
        }
    };

    const noCollaborators = collaborators.length === 0;

    return (
        <div className="wizard action-item-modal">
            <h3>Add Action Item</h3>
            <p>Create a commitment or follow-up for this initiative.</p>

            <div className="form-field -full">
                <label>What needs to be done?</label>
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && text.trim()) handleSave();
                    }}
                    autoFocus
                />
            </div>

            <DateInput
                id="dueDate"
                label="Due date (optional)"
                defaultValue={dueDate}
                updateOnChange={(id, date) => setDueDate(date)}
                minDate={new Date()}
            />

            <div className="form-field -full">
                <label>Assign to (optional)</label>
                <select
                    value={assigneeId}
                    onChange={(e) => setAssigneeId(e.target.value)}
                    disabled={noCollaborators}
                >
                    <option value="">
                        {noCollaborators ? "No collaborators yet" : "— Unassigned —"}
                    </option>
                    {collaborators.map((c) => (
                        <option key={c.userId} value={c.userId}>
                            {c.userName}
                        </option>
                    ))}
                </select>
            </div>

            {shortlist.length > 0 && (
                <div className="form-field -full">
                    <label>Link to opportunity (optional)</label>
                    <select
                        value={projectKey}
                        onChange={(e) => setProjectKey(e.target.value)}
                    >
                        <option value="">— None —</option>
                        {shortlist.map((opp) => (
                            <option key={opp.projectKey} value={opp.projectKey}>
                                {opp.name} · {opp.clientGroupName}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {error && <p className="txt -error">{error}</p>}

            <div className="finalize">
                <div className="button-group -right">
                    <button className="button -link" onClick={handleCancel} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="button -primary"
                        onClick={handleSave}
                        disabled={!text.trim() || saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddActionItemModal;
